import { useCallback, useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { http, formatApiError } from "@/lib/api";
import { formatDateTime, formatElapsed } from "@/lib/format";
import { useNow } from "@/hooks/useReconnectingSocket";
import { useRealtimeEvent } from "@/context/RealtimeContext";
import { StatusBadge } from "@/components/StatusBadge";
import { RequestDetailSheet } from "@/components/admin/RequestDetailSheet";
import { PageHeader, KpiCard } from "@/components/admin/PageBits";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkDeleteBar } from "@/components/admin/BulkDeleteBar";
import { PersonTypeBadge } from "@/components/admin/PersonTypeBadge";

export default function RequestsPage() {
  const [rows, setRows] = useState([]);
  const [fresh, setFresh] = useState(new Set());
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState([]);
  const toggleSel = (id, on) => setSelected((s) => (on ? [...s, id] : s.filter((x) => x !== id)));
  const allSel = rows.length > 0 && selected.length === rows.length;
  const now = useNow(1000);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/requests");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useRealtimeEvent((m) => {
    if (m.event === "resync") load();
    if (m.event === "request.created") {
      setRows((r) => [m.data, ...r.filter((x) => x.id !== m.data.id)]);
      setFresh((f) => new Set(f).add(m.data.id));
      setTimeout(() => setFresh((f) => { const n = new Set(f); n.delete(m.data.id); return n; }), 6000);
    }
    if (m.event === "request.updated") setRows((r) => r.map((x) => (x.id === m.data.id ? m.data : x)));
    if (m.event === "request.deleted") { setRows((r) => r.filter((x) => x.id !== m.data.id)); setSelected((s) => s.filter((x) => x !== m.data.id)); }
  });

  const count = (s) => rows.filter((r) => r.status === s).length;

  return (
    <div className="space-y-8">
      <PageHeader title="Solicitações recebidas" subtitle="Novas consultas aparecem automaticamente, em tempo real.">
        <BulkDeleteBar selected={selected} total={rows.length} onDone={() => { setSelected([]); load(); }} />
      </PageHeader>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Total" value={rows.length} testId="kpi-total" />
        <KpiCard label="Aguardando consulta" value={count("AGUARDANDO_CONSULTA")} testId="kpi-waiting" highlight />
        <KpiCard label="Pontuação enviada" value={count("PONTUACAO_DISPONIVEL")} testId="kpi-score" />
        <KpiCard label="Agendamentos" value={count("AGENDAMENTO_SOLICITADO")} testId="kpi-appointments" />
      </div>
      <div className="overflow-hidden rounded-md border border-white/[0.06] bg-[#141C2E]">
        <Table data-testid="requests-table">
          <TableHeader>
            <TableRow className="border-white/[0.06] hover:bg-transparent">
              <TableHead className="w-10"><Checkbox checked={allSel} onCheckedChange={(v) => setSelected(v ? rows.map((r) => r.id) : [])} className="border-white/30" data-testid="select-all-requests" /></TableHead>
              {["Identificador", "CPF / CNPJ", "Empresa", "Entrada", "Status", "Tempo decorrido", "Ações"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-16 text-center text-slate-500" data-testid="requests-empty">Nenhuma solicitação recebida ainda.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`request-row-${r.id}`} className={`border-white/[0.04] transition-colors hover:bg-white/[0.02] ${fresh.has(r.id) ? "row-fresh" : ""}`}>
                <TableCell><Checkbox checked={selected.includes(r.id)} onCheckedChange={(v) => toggleSel(r.id, v === true)} className="border-white/30" data-testid={`select-request-${r.id}`} /></TableCell>
                <TableCell className="font-mono-x text-xs text-slate-300" data-testid={`request-protocol-${r.id}`}>{r.protocolo}</TableCell>
                <TableCell className="whitespace-nowrap font-mono-x text-sm text-slate-100"><span className="mr-2"><PersonTypeBadge tipo={r.tipo} testId={`request-tipo-${r.id}`} /></span>{r.cnpj_fmt}</TableCell>
                <TableCell className="max-w-[260px]" data-testid={`request-company-${r.id}`}>
                  {r.company ? (
                    <>
                      <p className="truncate text-sm text-slate-100" title={r.company.razao_social}>{r.company.razao_social}</p>
                      {r.company.nome_fantasia && <p className="truncate text-xs text-slate-500" title={r.company.nome_fantasia}>{r.company.nome_fantasia}</p>}
                    </>
                  ) : (
                    <span className="text-xs text-slate-500">{r.tipo === "PF" ? "Pessoa física" : r.company_lookup === "PENDENTE" ? "Identificando…" : "Não identificada"}</span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm text-slate-400">{formatDateTime(r.created_at)}</TableCell>
                <TableCell><StatusBadge status={r.status} testId={`request-status-${r.id}`} /></TableCell>
                <TableCell className="font-mono-x text-sm text-slate-400" data-testid={`request-elapsed-${r.id}`}>{formatElapsed(r.created_at, now)}</TableCell>
                <TableCell>
                  <button onClick={() => setOpenId(r.id)} className="btn-ghost-sm" data-testid={`request-open-${r.id}`}><Eye className="h-3.5 w-3.5" /> Detalhes</button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <RequestDetailSheet requestId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
